import AuthProgressIndicator from '@/components/AuthProgressIndicator';
import { initDatadogRUM } from '@/services/datadog';
import { initMixpanel } from '@/services/mixpanel';
import { authUpdateWhenComeBackOnline } from '@/services/keycloak/helpers';
import keycloak from '@/services/keycloak/init';
import { AlertStore } from '@/store/AlertStore';
import { CaseStore } from '@/store/CaseStore';
import { UserStore } from '@/store/UserStore';
import { getCustomerName } from '@/utils/misc';
import { datadogRum } from '@datadog/browser-rum';
import mixpanel from 'mixpanel-browser';
import { FC, PropsWithChildren, useEffect, useState } from 'react';
import Intercom from '@intercom/messenger-js-sdk';
import { getUserHMAC } from '@/services/BackendAPI';

interface IProps {
  caseStore: CaseStore;
  alertStore: AlertStore;
  userStore: UserStore;
}

const AuthProvider: FC<PropsWithChildren<IProps>> = ({
  children,
  caseStore,
  alertStore,
  userStore,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const isSettingsRoute = window.location.pathname === '/settings';

  useEffect(() => {
    if (isSettingsRoute) return;

    const handleOnlineEvent = () => authUpdateWhenComeBackOnline(keycloak);
    window.addEventListener('online', handleOnlineEvent);

    keycloak.onTokenExpired = () => {
      keycloak.updateToken(30).catch(() => keycloak.login());
    };

    keycloak
      .init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
        pkceMethod: 'S256',
      })
      .then((authenticated) => {
        if (!authenticated) {
          // No active SSO session — fall back to a top-level redirect to log in.
          // Preserves the current URL so the user lands back where they started.
          // Chain the never-resolving promise so any login() rejection propagates
          // to the outer .catch() rather than hanging the app indefinitely.
          return keycloak
            .login({ redirectUri: window.location.href })
            .then(() => new Promise<never>(() => {}));
        }
        caseStore.initConfig();
        return keycloak.loadUserInfo() as Promise<any>;
      })
      .then((data: any) => {
        userStore.setUserData({ ...data, roles: keycloak.realmAccess?.roles });
        setLoading(false);

        initDatadogRUM();
        datadogRum.setUser({
          id: data.sub,
          name: data.name,
          username: data.preferred_username,
          email: data.email,
          customer: getCustomerName(),
          role: userStore.getPrimaryRole(),
        });
        initMixpanel();
        mixpanel.identify(data.sub);
        mixpanel.people.set({
          $name: data.name,
          $email: data.email,
          customer: getCustomerName(),
          role: userStore.getPrimaryRole(),
          permissions: keycloak.realmAccess?.roles,
        });

        getUserHMAC(data.sub).then((userHMAC) => {
          Intercom({
            api_base: 'https://api-iam.intercom.io',
            app_id: 'y30epli4',
            custom_launcher_selector: '#intercom_opener',
            user_id: data.sub,
            name: data.name,
            email: data.email,
            user_hash: userHMAC,
            hide_default_launcher: true,
          });
        });
      })
      .catch(() => {
        alertStore.error('Authentication failed');
        setLoading(false);
      });


    return () => window.removeEventListener('online', handleOnlineEvent);
  }, []);

  if (isSettingsRoute) {
    return <>{children}</>;
  }

  return <>{!loading ? children : <AuthProgressIndicator />}</>;
};

export default AuthProvider;
